import { apiClient } from './config.js';
import type {
  ImageDetail,
  ImageHistoryResponse,
  ImageListParams,
  ImageListResponse,
  ImageUpdateRequest,
  ReclassifyImageRequest,
  ReclassifyImageResult,
} from './types.js';

// Axios serialises array values into repeated query params when the URL
// is built — matching what FastAPI's Query(List[str]) expects. The
// default qs.parse mode here is "indices" which adds [] to keys; we
// want "repeat" to match the backend.
function buildListParams(params?: ImageListParams): URLSearchParams {
  const search = new URLSearchParams();
  if (!params) return search;
  const append = (key: string, value: unknown) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v !== undefined && v !== null && v !== '') {
          search.append(key, String(v));
        }
      });
    } else if (typeof value === 'boolean') {
      search.append(key, value ? 'true' : 'false');
    } else {
      search.append(key, String(value));
    }
  };
  append('announcement_id', params.announcement_id);
  append('entity_id', params.entity_id);
  append('image_type', params.image_type);
  append('image_role', params.image_role);
  append('ownership_type', params.ownership_type);
  append('source_type', params.source_type);
  append('min_quality', params.min_quality);
  append('max_quality', params.max_quality);
  append('low_confidence', params.low_confidence);
  append('has_description', params.has_description);
  append('search', params.search);
  append('limit', params.limit);
  append('offset', params.offset);
  return search;
}

export async function listImages(
  params?: ImageListParams,
): Promise<ImageListResponse> {
  const response = await apiClient.get<ImageListResponse>('/images', {
    params: buildListParams(params),
  });
  return response.data;
}

export async function getImage(imageId: string): Promise<ImageDetail> {
  const response = await apiClient.get<ImageDetail>(`/images/${imageId}`);
  return response.data;
}

export async function getImageHistory(
  imageId: string,
  limit = 20,
): Promise<ImageHistoryResponse> {
  const response = await apiClient.get<ImageHistoryResponse>(
    `/images/${imageId}/history`,
    { params: { limit } },
  );
  return response.data;
}

export async function updateImage(
  imageId: string,
  patch: ImageUpdateRequest,
): Promise<ImageDetail> {
  const response = await apiClient.patch<ImageDetail>(
    `/images/${imageId}`,
    patch,
  );
  return response.data;
}

export async function softDeleteImage(imageId: string): Promise<void> {
  await apiClient.delete(`/images/${imageId}`);
}

export async function reclassifyImage(
  imageId: string,
  body: ReclassifyImageRequest = {},
): Promise<ReclassifyImageResult> {
  const response = await apiClient.post<ReclassifyImageResult>(
    `/images/${imageId}/reclassify`,
    body,
  );
  return response.data;
}

export type CurationStatus = 'approved' | 'rejected' | 'pending' | null;

export interface SetCurationResult {
  image_id: string;
  before: unknown;
  after: unknown;
}

/**
 * Operator triage decision on a themeless (scraped) image — cynapse mig 060.
 *
 * Subject classes that may never flow automatically (project_render) are
 * usable only once approved, and a rejection always wins over the automatic
 * signals. cynapse recomputes eligibility on write, so the drawer's reason
 * codes reflect the decision as soon as the query refetches.
 */
export async function setImageCuration(
  imageId: string,
  status: CurationStatus,
): Promise<SetCurationResult> {
  const response = await apiClient.post<SetCurationResult>(
    `/images/${imageId}/curation`,
    { status },
  );
  return response.data;
}

export interface SourceEntityImagesResult {
  entity_id: string;
  job_id: string | null;
  queued: boolean;
  message: string;
}

export async function sourceEntityImages(
  entityId: string,
): Promise<SourceEntityImagesResult> {
  const response = await apiClient.post<SourceEntityImagesResult>(
    '/images/source-entity',
    { entity_id: entityId },
  );
  return response.data;
}
